import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {Dispatch, FC, SetStateAction, useMemo, useState} from 'react';
import {HandleThunkActionCreator} from 'react-redux';

import {AsyncHandlerOf, Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../libs/shared/helpers/src/types/customErrors';
import {Dictionary} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {CreateTagDialogContainer} from '../../components/common/dropdowns/tags/createTagDialogContainer';
import {TagTemplate} from '../../components/common/dropdowns/tags/tagEditorBody';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {CompanyModel} from '../../models/api/companyModel';
import {TagBulkActionTypesEnum} from '../../models/api/tagBulkActionModel';
import {TagBehaviorsEnum, TagModel} from '../../models/api/tagModel';
import {SettingsTagsUrl} from '../../models/url/settingsUrlModel';
import {
  InterfaceEventsEnum,
  SettingsTrackingTypesEnum,
  SingleTagCreationEntryPointsEnum,
  trackInterfaceEvent,
} from '../../services/tracking';
import {ResourceMap} from '../../store/appTypes';
import {nestTagsInsideNewParentTagAction} from '../../store/data/tags/tagBulkActions';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {ArchiveTagDialogContainer} from './archiveTagDialogContainer';
import {DeleteTagDialogContainer} from './deleteTagDialogContainer';
import {SettingsRestoreTagDialogContainer} from './settingsRestoreTagDialogContainer';
import {SettingsTagsNestUnderDialogContainer} from './settingsTagsNestUnderDialogContainer';

/*
 * Props.
 */

type DialogState = [boolean, Dispatch<SetStateAction<boolean>>];

interface SettingsTagsListDialogsProps {
  bulkSelectedTags: ReadonlyArray<TagModel>;
  company: CompanyModel;
  numberOfTagsInList: number;
  isInArchivedView: boolean;
  searchQuery: string;
  setTagToActOn: HandlerOf<TagModel | undefined>;
  tagsIdMap: ResourceMap<TagModel>;
  tagsUrl: SettingsTagsUrl;
  tagToActOn?: TagModel;
  resetState: Handler;
  expandedTags: Dictionary<boolean>;
  selectedTagIds: ReadonlyArray<number>;
  nestTagsInsideNewParentTag: HandleThunkActionCreator<typeof nestTagsInsideNewParentTagAction>;

  nestUnderDialogState: DialogState;
  deleteDialogState: DialogState;
  archiveDialogState: DialogState;
  restoreDialogState: DialogState;
}

/*
 * Component.
 */

export const SettingsTagsListDialogs: FC<SettingsTagsListDialogsProps> = (props) => {
  const {
    bulkSelectedTags,
    company,
    numberOfTagsInList,
    isInArchivedView,
    searchQuery,
    setTagToActOn,
    tagsIdMap,
    tagsUrl,
    tagToActOn,
    resetState,
    expandedTags,
    selectedTagIds,
    nestUnderDialogState,
    deleteDialogState,
    archiveDialogState,
    restoreDialogState,
  } = props;

  const hasAllSelected = useMemo(
    () => selectedTagIds.length === numberOfTagsInList,
    [selectedTagIds.length, numberOfTagsInList],
  );

  const [isNestUnderDialogOpen, setIsNestUnderDialogOpen] = nestUnderDialogState;
  const [isCreateTagDialogOpen, setIsCreateTagDialogOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = deleteDialogState;
  const [isArchiveConfirmationOpen, setIsArchiveConfirmationOpen] = archiveDialogState;
  const [isRestoreConfirmationOpen, setIsRestoreConfirmationOpen] = restoreDialogState;
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  const [tagCreationError, setTagCreationError] = useState<CustomError>();
  const {showBanner, updateBannerOnTagsBulk} = useSettingsBanners();

  const getNumberOfCheckedBoxes = () =>
    _(bulkSelectedTags)
      .reject((tag) => {
        if (!tag.parentTagId) {
          return false;
        }

        return Boolean(tag.parentTagId && !expandedTags[tag.parentTagId]);
      })
      .size();

  const onArchive = () => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_ARCHIVE, {
      settings_subpage: tagsUrl.tabPage,
      settings_type: isTeamNamespace(props.tagsUrl.namespace)
        ? SettingsTrackingTypesEnum.TEAM
        : SettingsTrackingTypesEnum.PERSONAL,
      namespace: props.tagsUrl.namespace,
      num_of_items: tagToActOn ? 0 : getNumberOfCheckedBoxes(),
      is_all_selected: hasAllSelected,
      entry_point: tagToActOn
        ? SingleTagCreationEntryPointsEnum.THREE_DOT_SELECTION
        : SingleTagCreationEntryPointsEnum.SETTINGS_MENU,
    });
    resetState();
    setIsArchiveConfirmationOpen(false);
  };
  const onCreateTag: AsyncHandlerOf<TagTemplate> = async (template) => {
    const newParentTag = await props.nestTagsInsideNewParentTag(template, (newTag) => {
      if (hasAllSelected) {
        return {
          type: TagBulkActionTypesEnum.NEST_UNDER,
          namespace: tagsUrl.namespace,
          parentTagId: newTag.id,
          searchTerm: searchQuery || '',
          searchOptions: {
            parentTagId: tagsUrl.tagId,
            behaviors: [TagBehaviorsEnum.CAN_UPDATE],
            excludeTagIds: _.compact([tagsUrl.tagId]),
            isArchived: isInArchivedView,
          },
        };
      }

      return {
        type: TagBulkActionTypesEnum.NEST_UNDER,
        namespace: tagsUrl.namespace,
        parentTagId: newTag.id,
        selectedTagIds: tagToActOn ? [tagToActOn.id] : selectedTagIds,
      };
    });
    resetState();
    setIsCreateTagDialogOpen(false);
    if (newParentTag) {
      showBanner({
        type: SettingsBannersEnum.LOADING,
        message: maybeRenderLoadingBannerMessage(newParentTag, selectedTagIds, tagToActOn),
        dismissAfter: 30 * 1000,
      });
      updateBannerOnTagsBulk({
        type: SettingsBannersEnum.SUCCESS,
        message: maybeRenderSuccessBannerMessage(newParentTag, selectedTagIds, tagToActOn),
      });
      trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_NEST, {
        settings_subpage: tagsUrl.tabPage,
        settings_type: isTeamNamespace(props.tagsUrl.namespace)
          ? SettingsTrackingTypesEnum.TEAM
          : SettingsTrackingTypesEnum.PERSONAL,
        namespace: props.tagsUrl.namespace,
        num_of_items: tagToActOn ? 0 : getNumberOfCheckedBoxes(),
        nest_type: 'add_subtag',
        is_all_selected: hasAllSelected,
        entry_point: tagToActOn
          ? SingleTagCreationEntryPointsEnum.THREE_DOT_SELECTION
          : SingleTagCreationEntryPointsEnum.SETTINGS_MENU,
      });
    }
  };

  const onDelete = () => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_DELETE, {
      settings_subpage: tagsUrl.tabPage,
      settings_type: isTeamNamespace(props.tagsUrl.namespace)
        ? SettingsTrackingTypesEnum.TEAM
        : SettingsTrackingTypesEnum.PERSONAL,
      namespace: props.tagsUrl.namespace,
      num_of_items: tagToActOn ? 0 : getNumberOfCheckedBoxes(),
      is_all_selected: hasAllSelected,
      entry_point: tagToActOn
        ? SingleTagCreationEntryPointsEnum.THREE_DOT_SELECTION
        : SingleTagCreationEntryPointsEnum.SETTINGS_MENU,
    });
    resetState();
    setIsDeleteConfirmationOpen(false);
  };

  const onNest = () => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_NEST, {
      settings_subpage: tagsUrl.tabPage,
      settings_type: isTeamNamespace(props.tagsUrl.namespace)
        ? SettingsTrackingTypesEnum.TEAM
        : SettingsTrackingTypesEnum.PERSONAL,
      namespace: props.tagsUrl.namespace,
      num_of_items: tagToActOn ? 0 : getNumberOfCheckedBoxes(),
      nest_type: 'nest_under',
      is_all_selected: hasAllSelected,
      entry_point: tagToActOn
        ? SingleTagCreationEntryPointsEnum.THREE_DOT_SELECTION
        : SingleTagCreationEntryPointsEnum.SETTINGS_MENU,
    });
    resetState();
    setIsNestUnderDialogOpen(false);
  };

  const onRestore = () => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_RESTORE, {
      settings_subpage: tagsUrl.tabPage,
      settings_type: isTeamNamespace(props.tagsUrl.namespace)
        ? SettingsTrackingTypesEnum.TEAM
        : SettingsTrackingTypesEnum.PERSONAL,
      namespace: props.tagsUrl.namespace,
      num_of_items: tagToActOn ? 0 : getNumberOfCheckedBoxes(),
      is_all_selected: hasAllSelected,
      entry_point: tagToActOn
        ? SingleTagCreationEntryPointsEnum.THREE_DOT_SELECTION
        : SingleTagCreationEntryPointsEnum.SETTINGS_MENU,
    });
    resetState();
    setIsRestoreConfirmationOpen(false);
  };

  const onCreateTagOkClick: AsyncHandlerOf<TagTemplate> = async (template) => {
    setIsCreatingTag(true);
    setTagCreationError(undefined);

    try {
      await onCreateTag(template);
      setIsCreateTagDialogOpen(false);
    } catch (error) {
      if (!(error instanceof CustomError)) {
        throw error;
      }

      // In case of conflict with an existing tag, display an error.
      setIsCreatingTag(false);
      setTagCreationError(error);
    }
  };

  const maybeRenderCreateTagDialog = () => {
    if (!isCreateTagDialogOpen) {
      return null;
    }

    return (
      <CreateTagDialogContainer
        namespace={tagsUrl.namespace}
        initialNamespace={tagsUrl.namespace}
        title={<Trans id="tag-create-title">Create new tag</Trans>}
        okButtonLabel={<Trans id="tag-create-ok">Create</Trans>}
        onOkClick={onCreateTagOkClick}
        onRequestClose={() => setIsCreateTagDialogOpen(false)}
        shouldHideNamespaceSelector
        shouldHideParentTagSelector
        isSaving={isCreatingTag}
        tagError={tagCreationError}
      />
    );
  };

  const makeInitialListParentTagId = () => {
    if (tagToActOn) {
      return tagToActOn.parentTagId;
    }

    const allSameParentTag =
      _(bulkSelectedTags)
        .map((t) => t.parentTagId)
        .uniq()
        .value().length === 1;

    if (allSameParentTag) {
      return bulkSelectedTags[0].parentTagId;
    }

    return undefined;
  };

  const maybeRenderNestUnderDialog = () => {
    if (!isNestUnderDialogOpen) {
      return null;
    }

    return (
      <SettingsTagsNestUnderDialogContainer
        company={company}
        isInArchivedView={isInArchivedView}
        tagsUrl={tagsUrl}
        hasAllSelected={hasAllSelected}
        tagsToActOn={bulkSelectedTags}
        onRequestClose={() => setIsNestUnderDialogOpen(false)}
        onNest={onNest}
        initialListParentTagId={makeInitialListParentTagId()}
        onAddTagClick={() => {
          setIsCreateTagDialogOpen(true);
          setIsNestUnderDialogOpen(false);
        }}
      />
    );
  };

  const maybeRenderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmationOpen) {
      return null;
    }

    return (
      <DeleteTagDialogContainer
        tagsUrl={props.tagsUrl}
        hasAllSelected={hasAllSelected}
        tagsToActOn={bulkSelectedTags}
        isInArchivedView={isInArchivedView}
        onDelete={onDelete}
        onRequestClose={() => {
          setIsDeleteConfirmationOpen(false);
          setTagToActOn(undefined);
        }}
        searchQuery={searchQuery}
      />
    );
  };

  const maybeRenderArchiveConfirmationDialog = () => {
    if (!isArchiveConfirmationOpen) {
      return null;
    }

    return (
      <ArchiveTagDialogContainer
        tagsUrl={props.tagsUrl}
        hasAllSelected={hasAllSelected}
        tagsToActOn={bulkSelectedTags}
        onArchive={onArchive}
        isInArchivedView={isInArchivedView}
        onRequestClose={() => {
          setIsArchiveConfirmationOpen(false);
          setTagToActOn(undefined);
        }}
        searchQuery={searchQuery}
      />
    );
  };

  const maybeRenderRestoreConfirmationDialog = () => {
    if (!isRestoreConfirmationOpen) {
      return null;
    }

    return (
      <SettingsRestoreTagDialogContainer
        tagsUrl={props.tagsUrl}
        hasAllSelected={hasAllSelected}
        tagsToActOn={bulkSelectedTags}
        onRestore={onRestore}
        isInArchivedView={isInArchivedView}
        searchQuery={searchQuery}
        searchTagsIdMap={tagsIdMap}
        onRequestClose={() => {
          setIsRestoreConfirmationOpen(false);
          setTagToActOn(undefined);
        }}
      />
    );
  };

  return (
    <>
      {maybeRenderCreateTagDialog()}
      {maybeRenderNestUnderDialog()}
      {maybeRenderDeleteConfirmDialog()}
      {maybeRenderArchiveConfirmationDialog()}
      {maybeRenderRestoreConfirmationDialog()}
    </>
  );
};

function maybeRenderLoadingBannerMessage(
  newParentTag: TagModel,
  selectedTagIds: ReadonlyArray<number>,
  tagToActOn?: TagModel,
) {
  const tagIdsToActOn = tagToActOn ? [tagToActOn.id] : selectedTagIds;

  if (tagIdsToActOn.length === 0) {
    return null;
  }

  if (tagIdsToActOn.length === 1 && tagToActOn) {
    return (
      <Trans id="settingstags-nestsingle-loading">
        Nesting &quot;{tagToActOn.name}&quot; under &quot;{newParentTag.name}&quot;
      </Trans>
    );
  }

  return (
    <Trans id="settingstags-nestmany-loading">
      Nesting {tagIdsToActOn.length} tags under &quot;{newParentTag.name}&quot;
    </Trans>
  );
}
function maybeRenderSuccessBannerMessage(
  newParentTag: TagModel,
  selectedTagIds: ReadonlyArray<number>,
  tagToActOn?: TagModel,
) {
  const tagIdsToActOn = tagToActOn ? [tagToActOn.id] : selectedTagIds;

  if (tagIdsToActOn.length === 0) {
    return null;
  }

  if (tagIdsToActOn.length === 1 && tagToActOn) {
    return (
      <Trans id="settingstags-nestsingle-success">
        Nested &quot;{tagToActOn.name}&quot; under &quot;{newParentTag.name}&quot;
      </Trans>
    );
  }

  return (
    <Trans id="settingstags-nestmany-success">
      Nested {tagIdsToActOn.length} tags under &quot;{newParentTag.name}&quot;
    </Trans>
  );
}
